import React, { useState, useEffect } from 'react';
import {
    getAmoMetadata,
    syncAmoMetadata,
    getAmoConversionConfig,
    updateAmoConversionConfig,
    triggerAmoLeadSync
} from '../api';

const AmoConversionSettings = ({ clientId }) => {
    const [metadata, setMetadata] = useState(null);
    const [config, setConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    // States for sync testing
    const [syncDates, setSyncDates] = useState({ start: '', end: '' });

    useEffect(() => {
        if (clientId) {
            loadData();
        }
    }, [clientId]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            try {
                const meta = await getAmoMetadata(clientId);
                setMetadata(meta);
            } catch (e) {
                // Metadata might not exist yet
            }

            const conf = await getAmoConversionConfig(clientId);
            setConfig(conf);
        } catch (error) {
            setStatusMessage(`Ошибка загрузки: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSyncMetadata = async () => {
        setIsLoading(true);
        setStatusMessage('Синхронизация структуры AMO (воронки, поля)...');
        try {
            const meta = await syncAmoMetadata(clientId);
            setMetadata(meta);
            setStatusMessage('Структура AMO успешно обновлена.');
        } catch (error) {
            setStatusMessage(`Ошибка синхронизации: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRunSync = async () => {
        if (!syncDates.start || !syncDates.end) {
            setStatusMessage('Заполните даты для синхронизации');
            return;
        }

        setIsLoading(true);
        setStatusMessage('Запуск синхронизации заявок...');

        try {
            const startUnix = Math.floor(new Date(syncDates.start).getTime() / 1000);
            const endUnix = Math.floor(new Date(syncDates.end).getTime() / 1000) + 86399; // end of day

            const res = await triggerAmoLeadSync(clientId, startUnix, endUnix);
            setStatusMessage(res.message || 'Синхронизация завершена!');
        } catch (error) {
            setStatusMessage(`Ошибка при синхронизации заявок: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    if (!clientId) return null;

    return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg mt-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg text-gray-800">Настройки конверсии AMO CRM (v5)</h3>
                <button
                    onClick={handleSyncMetadata}
                    disabled={isLoading}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                >
                    {isLoading ? 'Загрузка...' : 'Обновить структуру из AMO'}
                </button>
            </div>

            {statusMessage && (
                <div className="mb-4 p-2 bg-yellow-50 text-yellow-800 rounded text-sm border border-yellow-200">
                    {statusMessage}
                </div>
            )}

            {/* Warning if no metadata */}
            {!metadata ? (
                <div className="p-4 bg-orange-50 border border-orange-200 text-orange-800 rounded mb-4">
                    Структура AMO CRM (Списки полей, воронок) еще не загружена. Нажмите "Обновить структуру из AMO".
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="p-3 bg-white border rounded">
                            <span className="font-bold text-gray-600 block mb-1">Воронок:</span>
                            {metadata.pipelines?.length || 0} шт.
                        </div>
                        <div className="p-3 bg-white border rounded">
                            <span className="font-bold text-gray-600 block mb-1">Полей:</span>
                            {metadata.fields?.length || 0} шт.
                        </div>
                    </div>

                    <div className="p-4 bg-white border rounded">
                        <h4 className="font-semibold text-md mb-2">Ручной запуск синхронизации лидов</h4>
                        <div className="flex gap-2 items-end">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">От (созданы после)</label>
                                <input
                                    type="date"
                                    className="border p-2 rounded text-sm w-full"
                                    value={syncDates.start}
                                    onChange={e => setSyncDates({ ...syncDates, start: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">До</label>
                                <input
                                    type="date"
                                    className="border p-2 rounded text-sm w-full"
                                    value={syncDates.end}
                                    onChange={e => setSyncDates({ ...syncDates, end: e.target.value })}
                                />
                            </div>
                            <button
                                onClick={handleRunSync}
                                disabled={isLoading}
                                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium h-[38px] flex items-center"
                            >
                                Синхронизировать
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                            Система автоматически синхронизирует данные раз в час по настроенным формулам (крон).
                            Используйте эту кнопку для первоначальной выгрузки за старые месяцы.
                        </p>
                    </div>

                    {/* Note: In a complete implementation, here goes the JSON/UI builder for config.field_mapping and config.conversion_formulas. We provide a simplified JSON editor for the first step to allow superadmins to paste the template config. */}
                    <div className="p-4 bg-white border rounded">
                        <h4 className="font-semibold text-md mb-2">Конфигурация (JSON Формат)</h4>
                        <p className="text-xs text-gray-500 mb-2">
                            Здесь настраиваются ID полей, маппинг администраторов и логика воронок (numerator/denominator).
                        </p>
                        <textarea
                            className="w-full h-64 p-2 text-xs font-mono border rounded bg-gray-50"
                            value={config ? JSON.stringify(config, null, 2) : ''}
                            onChange={(e) => {
                                try {
                                    const val = JSON.parse(e.target.value);
                                    setConfig(val);
                                } catch (err) {
                                    // just ignore syntax errors while typing
                                }
                            }}
                        />
                        <button
                            onClick={async () => {
                                setIsLoading(true);
                                try {
                                    await updateAmoConversionConfig(clientId, config);
                                    setStatusMessage('Конфигурация успешно сохранена.');
                                } catch (error) {
                                    setStatusMessage(`Ошибка сохранения: ${error.message}`);
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            disabled={isLoading}
                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
                        >
                            Сохранить JSON конфигурацию
                        </button>
                    </div>

                </div>
            )}
        </div>
    );
};

export default AmoConversionSettings;
